import { circle, distance } from "@turf/turf";
import { FruitTraceability, statementFruitSites } from "../../models/traceability/FruitTraceability";
import { getTraceabilityData } from "./traceabilityTypes";

/**
 * Model behind the purchase map: every fruit source of every contract in the
 * period, each one resolved to a position and a straight-line distance to the mill.
 *
 * Supplier names never reach this layer — the legend maps pin letters to generic
 * aliases ("Source 1", "Source 2") while the GPS coordinates and the flow
 * geometry stay fully visible.
 *
 * Sources are deliberately not split into farms and ramps: seller statements
 * carry a traceability label (how precisely the origin is known), not a supplier
 * type, so any such split would be a guess shown as a fact.
 */

export const FLOW_COLORS = {
    mill: '#0d5c6b',
    flow: '#d92b2b',
    zone: '#56055d',
};

export type MapSource = {
    contractId: number
    statementId?: number
    coordinates: [number, number]
    /** Traceability label, echoing the "Traceable to" column. */
    label?: string
    /** Traceability colour, so the overview dots read like the table pills. */
    color: string
    /** Land plot outline, when the statement carries one. */
    polygon?: number[][]
    /** Straight-line mill distance in km; undefined when the mill has no position. */
    distanceKm?: number
    /** Pin letter within its own contract: A, B … Z, AA. */
    letter: string
    /** 1-based position within its contract, for the anonymised legend label. */
    aliasIndex: number
}

/** Spreadsheet-style letters, so a contract with more than 26 sources still reads. */
export const pinLetter = (index: number): string => {
    let n = index;
    let out = '';
    do {
        out = String.fromCharCode(65 + (n % 26)) + out;
        n = Math.floor(n / 26) - 1;
    } while (n >= 0);
    return out;
};

export const isValidCoordinate = (coords: any): coords is [number, number] =>
    Array.isArray(coords) && coords.length === 2 && coords.every(c => Number.isFinite(Number(c)));

/** Mills reach this page from the profile (`position`) or the legacy model. */
export const millCoordinates = (mill: any): [number, number] | undefined => {
    const coords = (mill?.position ?? mill?.positionepsg4326)?.coordinates;
    return isValidCoordinate(coords) ? [Number(coords[0]), Number(coords[1])] : undefined;
};

/** Sources of one contract: every fruit site of every statement, in statement order. */
export const buildContractSources = (
    contract: FruitTraceability | undefined,
    millCoords?: [number, number]
): MapSource[] => {
    if (!contract) return [];

    return (contract.seller_statements ?? [])
        .flatMap((statement: any) => statementFruitSites(statement).map(site => ({ statement, site })))
        .map(({ statement, site }, index: number): MapSource => ({
            contractId: contract.contract_id,
            statementId: statement?.statement_id,
            coordinates: site.coordinates,
            label: statement?.label,
            color: getTraceabilityData(statement?.label?.toLowerCase()).color,
            polygon: statement?.UL_polygon?.coordinates?.[0],
            distanceKm: millCoords ? distance(millCoords, site.coordinates, { units: 'kilometers' }) : undefined,
            letter: pinLetter(index),
            aliasIndex: index + 1,
        }));
};

export const buildAllSources = (
    contracts: FruitTraceability[],
    millCoords?: [number, number]
): MapSource[] =>
    (contracts ?? []).flatMap(contract => buildContractSources(contract, millCoords));

export const averageDistanceKm = (sources: MapSource[]): number | undefined => {
    const measured = sources.filter(s => s.distanceKm !== undefined);
    if (measured.length === 0) return undefined;
    return measured.reduce((sum, s) => sum + s.distanceKm, 0) / measured.length;
};

/* ─── GeoJSON ─── */

const featureCollection = (features: any[]): any => ({ type: 'FeatureCollection', features });

export const sourcePoints = (sources: MapSource[]): any =>
    featureCollection(sources.map(source => ({
        type: 'Feature',
        properties: { contractId: source.contractId, color: source.color },
        geometry: { type: 'Point', coordinates: source.coordinates },
    })));

/**
 * One line per source, drawn source → mill so an animated dash pattern reads as
 * fruit flowing into the mill rather than away from it.
 */
export const flowLines = (sources: MapSource[], millCoords?: [number, number]): any =>
    featureCollection(millCoords
        ? sources.map(source => ({
            type: 'Feature',
            properties: { contractId: source.contractId },
            geometry: { type: 'LineString', coordinates: [source.coordinates, millCoords] },
        }))
        : []);

export const sourcePlots = (sources: MapSource[]): any =>
    featureCollection(sources
        .filter(source => Array.isArray(source.polygon) && source.polygon.length > 2)
        .map(source => ({
            type: 'Feature',
            properties: { contractId: source.contractId, color: source.color },
            geometry: { type: 'Polygon', coordinates: [source.polygon] },
        })));

/**
 * The supply zone: a circle centred on the mill reaching its furthest source.
 * Drawn as a real geodesic polygon, so it stays true at any zoom.
 */
export const supplyZone = (sources: MapSource[], millCoords?: [number, number]): any => {
    const radiusKm = sources.reduce((max, s) => Math.max(max, s.distanceKm ?? 0), 0);
    if (!millCoords || radiusKm <= 0) return featureCollection([]);
    return featureCollection([circle(millCoords, radiusKm, { steps: 96, units: 'kilometers' })]);
};

export const supplyZoneRadiusKm = (sources: MapSource[]): number =>
    sources.reduce((max, s) => Math.max(max, s.distanceKm ?? 0), 0);

/** `[lng, lat]` formatted for the legend — latitude first, as GPS is read aloud. */
export const formatCoordinates = (coords: [number, number]): string =>
    `${coords[1].toFixed(5)}, ${coords[0].toFixed(5)}`;
